import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Card, Table, Button, Modal, Form, Alert, Row, Col, Tabs, Tab, Badge } from "react-bootstrap";
import { FaPlus, FaFileInvoiceDollar, FaCheck, FaBoxOpen, FaImage, FaFilter } from "react-icons/fa";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { getAllInvoicesApi, getAllPurchasesApi, createPurchaseInvoiceApi, cancelPurchaseInvoiceApi, getAllInventoryApi, getAllCategoriesApi } from "./service/api_service";
import ImageViewerButton from "../layout/ImageViewerButton";
import { useAuth } from "../authen/AuthContext";
import Swal from "sweetalert2";

export default function Purchase() {
  const { user: currentUser } = useAuth();
  const token = localStorage.getItem("token");

  const [activeTab, setActiveTab] = useState("invoices");

  // Data states
  const [invoices, setInvoices] = useState([]);
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [searchVendor, setSearchVendor] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [searchItem, setSearchItem] = useState("");

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [invoiceImageFile, setInvoiceImageFile] = useState(null);
  const [invoiceImagePreview, setInvoiceImagePreview] = useState("");
  
  // Vendor specific state for UI
  const [selectedVendor, setSelectedVendor] = useState("");
  const [customVendor, setCustomVendor] = useState("");

  const [formData, setFormData] = useState({
    invoice_date: new Date().toISOString().substring(0, 10),
    attachment_url: "",
    purchases: [{ inventory_item_id: "", quantity: "", unit_cost: "", isNew: false, name: "", type: "direct_cost", category_id: "", unit: "", filterCategoryId: "ALL", branch_id: currentUser?.default_branch_id || "" }]
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [invRes, purRes, itemsRes, catRes] = await Promise.all([
        getAllInvoicesApi(token),
        getAllPurchasesApi(token),
        getAllInventoryApi(token),
        getAllCategoriesApi(token)
      ]);
      setInvoices(invRes.data);
      setPurchasedItems(purRes.data);
      setInventoryItems(itemsRes.data);
      setCategories(catRes.data);
    } catch (err) {
      setError("Failed to load data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancelInvoice = async (id) => {
    try {
      const res = await Swal.fire({
        title: 'คุณแน่ใจหรือไม่ที่จะยกเลิกบิลซื้อนี้?',
        text: 'ระบบจะหักสต๊อกที่รับเข้ามาออก หากสินค้าถูกเบิกไปแล้วจะไม่สามารถยกเลิกได้',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'ยกเลิกบิล (Void)',
        cancelButtonText: 'ปิด',
        confirmButtonColor: '#dc3545'
      });

      if (res.isConfirmed) {
        setLoading(true);
        await cancelPurchaseInvoiceApi(token, id);
        await Swal.fire('สำเร็จ', 'ยกเลิกบิลเรียบร้อยแล้ว', 'success');
        setShowViewModal(false);
        fetchData();
      }
    } catch (err) {
      Swal.fire('ข้อผิดพลาด', err.response?.data?.message || err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Derived unique vendors
  const uniqueVendors = useMemo(() => {
    const vendors = invoices.map(i => i.vendor).filter(v => v);
    return [...new Set(vendors)];
  }, [invoices]);

  // Derived unique items for filter dropdown
  const uniqueItems = useMemo(() => {
    const items = purchasedItems.map(p => p.item_name).filter(i => i);
    return [...new Set(items)];
  }, [purchasedItems]);

  // Derived Item options (for dropdown)
  const itemOptions = useMemo(() => {
    return [
      { id: "NEW", name: "+ Create New Item", unit: "" },
      ...inventoryItems
    ];
  }, [inventoryItems]);

  const handleShowAdd = () => {
    setSelectedVendor("");
    setCustomVendor("");
    setInvoiceImageFile(null);
    setInvoiceImagePreview("");
    setFormData({
      invoice_date: new Date().toISOString().substring(0, 10),
      attachment_url: "",
      purchases: [{ inventory_item_id: "", quantity: "", unit_cost: "", total_cost: "", isNew: false, name: "", type: "direct_cost", category_id: "", unit: "", filterCategoryId: "ALL", branch_id: currentUser?.default_branch_id || "", isDistributed: false, distributions: [] }]
    });
    setShowAddModal(true);
    setError("");
  };

  const handleViewInvoice = (inv) => {
    const itemsInInvoice = purchasedItems.filter(p => p.inventory_invoice_id === inv.id);
    setViewInvoice({ ...inv, purchases: itemsInInvoice });
    setShowViewModal(true);
  };

  const handleAddRow = () => {
    setFormData(prev => ({
      ...prev,
      purchases: [...prev.purchases, { inventory_item_id: "", quantity: "", unit_cost: "", total_cost: "", isNew: false, name: "", type: "direct_cost", category_id: "", unit: "", filterCategoryId: "ALL", branch_id: currentUser?.default_branch_id || "", isDistributed: false, distributions: [] }]
    }));
  };

  const handleRemoveRow = (index) => {
    setFormData(prev => ({
      ...prev,
      purchases: prev.purchases.filter((_, i) => i !== index)
    }));
  };

  const handleChangePurchase = (index, field, value) => {
    const newPurchases = [...formData.purchases];
    if (field === "inventory_item_obj") {
      const selected = value;
      if (!selected) {
        newPurchases[index] = { ...newPurchases[index], inventory_item_id: "", isNew: false, unit: "", name: "" };
      } else if (selected.__isNew__ || !inventoryItems.find(i => String(i.id) === String(selected.value))) {
        newPurchases[index] = { ...newPurchases[index], inventory_item_id: "NEW", isNew: true, unit: "", name: selected.value };
      } else {
        const selectedItem = inventoryItems.find(i => String(i.id) === String(selected.value));
        newPurchases[index] = { 
          ...newPurchases[index], 
          inventory_item_id: selected.value, 
          isNew: false, 
          unit: selectedItem ? selectedItem.unit : "",
          name: ""
        };
      }
    } else {
      newPurchases[index][field] = value;
    }

    // Auto-calculate unit_cost to 6 decimals based on total_cost and quantity
    if (field === "total_cost" || field === "quantity") {
      const qty = Number(newPurchases[index].quantity);
      const total = Number(newPurchases[index].total_cost);
      if (qty > 0 && total >= 0) {
        newPurchases[index].unit_cost = (total / qty).toFixed(6);
      } else {
        newPurchases[index].unit_cost = "";
      }
    }

    setFormData(prev => ({ ...prev, purchases: newPurchases }));
  };

  const handleToggleDistribute = (index) => {
    const newPurchases = [...formData.purchases];
    newPurchases[index].isDistributed = !newPurchases[index].isDistributed;
    if (newPurchases[index].isDistributed && newPurchases[index].distributions.length === 0) {
      // Add first distribution row with default branch
      newPurchases[index].distributions = [{ branch_id: currentUser?.default_branch_id || "", quantity: "" }];
    }
    setFormData(prev => ({ ...prev, purchases: newPurchases }));
  };

  const handleAddDistribution = (index) => {
    const newPurchases = [...formData.purchases];
    newPurchases[index].distributions.push({ branch_id: "", quantity: "" });
    setFormData(prev => ({ ...prev, purchases: newPurchases }));
  };

  const handleRemoveDistribution = (pIndex, dIndex) => {
    const newPurchases = [...formData.purchases];
    newPurchases[pIndex].distributions.splice(dIndex, 1);
    if (newPurchases[pIndex].distributions.length === 0) {
      newPurchases[pIndex].isDistributed = false;
    }
    setFormData(prev => ({ ...prev, purchases: newPurchases }));
  };

  const handleChangeDistribution = (pIndex, dIndex, field, value) => {
    const newPurchases = [...formData.purchases];
    newPurchases[pIndex].distributions[dIndex][field] = value;
    setFormData(prev => ({ ...prev, purchases: newPurchases }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.invoice_date) {
      setError("Date is required");
      return;
    }
    
    // Resolve Vendor
    let finalVendor = selectedVendor === "NEW" ? customVendor : selectedVendor;
    if (!finalVendor) finalVendor = null;

    // Filter and validate rows, and flatten distributions
    let validPurchases = [];
    let hasError = false;

    formData.purchases.forEach(p => {
      if (p.isNew && (!p.name || !p.category_id || !p.unit)) return;
      if (!p.isNew && !p.inventory_item_id) return;
      if (Number(p.quantity) <= 0 || Number(p.unit_cost) < 0 || p.unit_cost === "") return;

      if (p.isDistributed) {
        let distTotalQty = 0;
        p.distributions.forEach(d => {
          if (Number(d.quantity) > 0 && d.branch_id) {
            distTotalQty += Number(d.quantity);
            validPurchases.push({
              ...p,
              branch_id: d.branch_id,
              quantity: d.quantity,
              total_cost: (Number(d.quantity) * Number(p.unit_cost)).toFixed(2)
            });
          }
        });
        if (Math.abs(distTotalQty - Number(p.quantity)) > 0.01) {
          setError(`Distributed quantity (${distTotalQty}) does not match total quantity (${p.quantity}) for item.`);
          hasError = true;
        }
      } else {
        validPurchases.push(p);
      }
    });
    
    if (hasError) return;

    if (validPurchases.length === 0) {
      setError("Please add at least one valid purchase item (quantity > 0, valid cost, complete new item details)");
      return;
    }

    try {
      const payload = { 
        ...formData, 
        vendor: finalVendor, 
        purchases: validPurchases 
      };

      const formDataPayload = new FormData();
      formDataPayload.append("purchaseData", JSON.stringify(payload));
      if (invoiceImageFile) {
        formDataPayload.append("invoice_image", invoiceImageFile);
      }

      await createPurchaseInvoiceApi(token, formDataPayload);
      setShowAddModal(false);
      fetchData();
    } catch (err) {
      setError("Failed to create invoice");
    }
  };

  // Filtering
  const filteredInvoices = invoices.filter(inv => {
    const matchVendor = searchVendor ? (inv.vendor || "").toLowerCase() === searchVendor.toLowerCase() : true;
    const matchDate = searchDate ? inv.invoice_date.startsWith(searchDate) : true;
    return matchVendor && matchDate;
  });

  const filteredPurchases = purchasedItems.filter(p => {
    const matchVendor = searchVendor ? (p.vendor || "").toLowerCase() === searchVendor.toLowerCase() : true;
    const matchDate = searchDate ? p.invoice_date.startsWith(searchDate) : true;
    const matchItem = searchItem ? (p.item_name || "").toLowerCase() === searchItem.toLowerCase() : true;
    return matchVendor && matchDate && matchItem;
  });

  const darkBlueStyle = { backgroundColor: "#1e3c72", borderColor: "#1e3c72", color: "white" };
  const textDarkBlueStyle = { color: "#1e3c72" };

  return (
    <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100%" }}>
      
      {/* Header & Filters Inline */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="fw-bold mb-0" style={textDarkBlueStyle}><FaFileInvoiceDollar className="me-2" /> Purchases</h2>
          <p className="text-muted mb-0 mt-1">Manage supplier invoices and incoming stock</p>
        </div>
        
        <div className="d-flex flex-wrap align-items-center gap-2">
          {/* Filters */}
          <Form.Select 
            style={{ width: '200px' }} 
            className="shadow-sm border-0 bg-white"
            value={searchVendor} 
            onChange={e => setSearchVendor(e.target.value)}
          >
            <option value="">All Vendors</option>
            {uniqueVendors.map((v, idx) => <option key={idx} value={v}>{v}</option>)}
          </Form.Select>

          {activeTab === "items" && (
            <Form.Select 
              style={{ width: '200px' }} 
              className="shadow-sm border-0 bg-white"
              value={searchItem} 
              onChange={e => setSearchItem(e.target.value)}
            >
              <option value="">All Items</option>
              {uniqueItems.map((i, idx) => <option key={idx} value={i}>{i}</option>)}
            </Form.Select>
          )}

          <Form.Control 
            type="date" 
            style={{ width: '160px' }} 
            className="shadow-sm border-0 bg-white"
            value={searchDate} 
            onChange={e => setSearchDate(e.target.value)}
          />

          {/* Add Button */}
          <Button onClick={handleShowAdd} className="px-4 shadow-sm ms-2" style={{ borderRadius: '8px', ...darkBlueStyle }}>
            <FaPlus className="me-2" /> Record Purchase
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k)} className="px-3 pt-3 border-bottom-0">
          <Tab eventKey="invoices" title={<span><FaFileInvoiceDollar className="me-2"/> By Invoice</span>}>
            <Table responsive hover className="mb-0" style={{ cursor: "pointer" }}>
              <thead className="bg-light text-muted">
                <tr>
                  <th className="ps-4">ID</th>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Recorded By</th>
                  <th className="text-end pe-4">Items Count</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-5 text-muted">Loading data...</td></tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-5 text-muted">No invoices found.</td></tr>
                ) : (
                  filteredInvoices.map((inv) => {
                    const count = purchasedItems.filter(p => p.inventory_invoice_id === inv.id).length;
                    return (
                      <tr key={inv.id} className="align-middle" onClick={() => handleViewInvoice(inv)}>
                        <td className="ps-4 fw-bold" style={textDarkBlueStyle}>INV-{inv.id.toString().padStart(4, '0')}</td>
                        <td>{new Date(inv.invoice_date).toLocaleDateString()}</td>
                        <td className="fw-medium">
                          {inv.vendor || <span className="text-muted fst-italic">Unknown</span>}
                          {inv.status === 'cancelled' && <Badge bg="danger" className="ms-2">Cancelled</Badge>}
                        </td>
                        <td>{inv.creator_name || 'System'}</td>
                        <td className="text-end pe-4 text-muted">{count} items</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </Tab>
          
          <Tab eventKey="items" title={<span><FaBoxOpen className="me-2"/> By Item</span>}>
            <Table responsive hover className="mb-0">
              <thead className="bg-light text-muted">
                <tr>
                  <th className="ps-4">Invoice</th>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Item Name</th>
                  <th className="text-end">Quantity</th>
                  <th>Unit</th>
                  <th className="text-end pe-4">Unit Cost (฿)</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {loading ? (
                  <tr><td colSpan="7" className="text-center py-5 text-muted">Loading data...</td></tr>
                ) : filteredPurchases.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-5 text-muted">No purchased items found.</td></tr>
                ) : (
                  filteredPurchases.map((p) => {
                    const parentInv = invoices.find(inv => inv.id === p.inventory_invoice_id);
                    return (
                      <tr key={p.id} className="align-middle" onClick={() => parentInv && handleViewInvoice(parentInv)} style={{ cursor: parentInv ? "pointer" : "default" }}>
                        <td className="ps-4 fw-bold text-secondary">INV-{p.inventory_invoice_id.toString().padStart(4, '0')}</td>
                        <td>{new Date(p.invoice_date).toLocaleDateString()}</td>
                        <td>{p.vendor || '-'}</td>
                        <td className="fw-medium">{p.item_name}</td>
                        <td className="text-end fw-bold text-success">+{parseFloat(p.quantity)}</td>
                        <td className="text-muted">{p.unit}</td>
                        <td className="text-end pe-4">{parseFloat(p.unit_cost).toFixed(2)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </Tab>
        </Tabs>
      </Card>

      {/* View Invoice Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton className="border-0 bg-light pb-3">
          <Modal.Title className="fw-bold" style={textDarkBlueStyle}>
            Invoice Details {viewInvoice && `(INV-${viewInvoice.id.toString().padStart(4, '0')})`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {viewInvoice && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <p className="mb-1 text-muted small fw-bold text-uppercase">Vendor</p>
                  <h5 className="fw-bold text-dark">
                    {viewInvoice.vendor || "Unknown Vendor"}
                    {viewInvoice.status === 'cancelled' && <Badge bg="danger" className="ms-2">Cancelled</Badge>}
                  </h5>
                </Col>
                <Col md={6} className="text-md-end">
                  <p className="mb-1 text-muted small fw-bold text-uppercase">Invoice Date</p>
                  <h5 className="fw-bold text-dark">{new Date(viewInvoice.invoice_date).toLocaleDateString()}</h5>
                </Col>
              </Row>

              {(viewInvoice.invoice_image_url || viewInvoice.attachment_url) && (
                <div className="mb-4 p-3 bg-light rounded text-center d-flex justify-content-center">
                  <ImageViewerButton 
                    imageUrl={viewInvoice.invoice_image_url || viewInvoice.attachment_url} 
                    buttonText="ดูรูปถ่ายบิล/ใบเสร็จ" 
                  />
                </div>
              )}

              <h6 className="fw-bold text-muted text-uppercase mb-3" style={{ fontSize: '0.85rem' }}>Items Purchased</h6>
              <Table size="sm" responsive className="border rounded">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-3">Item Name</th>
                    <th>Branch</th>
                    <th className="text-end">Qty</th>
                    <th>Unit</th>
                    <th className="text-end pe-3">Unit Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {viewInvoice.purchases.map(p => (
                    <tr key={p.id}>
                      <td className="ps-3 fw-medium">{p.item_name}</td>
                      <td><Badge bg="info" className="fw-normal">{p.branch_name || '-'}</Badge></td>
                      <td className="text-end fw-bold text-success">+{parseFloat(p.quantity)}</td>
                      <td className="text-muted">{p.unit}</td>
                      <td className="text-end pe-3">{parseFloat(p.unit_cost).toFixed(2)} ฿</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-3 bg-light d-flex justify-content-between">
          {viewInvoice && viewInvoice.status !== 'cancelled' ? (
            <Button variant="outline-danger" onClick={() => handleCancelInvoice(viewInvoice.id)}>
              ยกเลิกบิล (Void)
            </Button>
          ) : (
            <div></div>
          )}
          <Button variant="secondary" onClick={() => setShowViewModal(false)} className="px-4 rounded">Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="xl" backdrop="static">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="border-0 bg-light pb-3">
            <Modal.Title className="fw-bold" style={textDarkBlueStyle}><FaPlus className="me-2"/> Record New Purchase</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-light pt-0" style={{ minHeight: '70vh', maxHeight: '70vh', overflowY: 'auto' }}>
            
            {/* Invoice Info Card */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
              <Card.Body className="p-3">
                <h6 className="fw-bold text-muted mb-2 text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '1px' }}>Invoice Details</h6>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-medium small text-muted mb-1">Vendor Name (Optional)</Form.Label>
                      <CreatableSelect
                        isClearable
                        placeholder="Select or type new vendor..."
                        options={uniqueVendors.map(v => ({ value: v, label: v }))}
                        value={selectedVendor ? { value: selectedVendor, label: selectedVendor } : null}
                        onChange={(selected) => {
                          setSelectedVendor(selected ? selected.value : "");
                        }}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: '31px',
                            height: '31px',
                            fontSize: '0.875rem',
                            borderRadius: '0.25rem',
                            borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
                            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
                            '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#dee2e6' }
                          }),
                          valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                          input: (base) => ({ ...base, margin: '0', padding: '0' }),
                          indicatorsContainer: (base) => ({ ...base, height: '29px' }),
                          menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 9999 })
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-medium small text-muted mb-1">Invoice Date</Form.Label>
                      <Form.Control size="sm" type="date" value={formData.invoice_date} onChange={e => setFormData({...formData, invoice_date: e.target.value})} required />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label className="fw-medium small text-muted mb-1">Receipt / Invoice Image</Form.Label>
                      <div>
                        {invoiceImagePreview ? (
                          <ImageViewerButton 
                            imageUrl={invoiceImagePreview}
                            onUpdate={(file) => {
                              setInvoiceImageFile(file);
                              setInvoiceImagePreview(URL.createObjectURL(file));
                            }}
                            onDelete={() => {
                              setInvoiceImageFile(null);
                              setInvoiceImagePreview("");
                            }}
                            buttonText="ดูรูปใบเสร็จ"
                          />
                        ) : (
                          <Form.Label className="btn btn-outline-secondary btn-sm m-0" style={{ cursor: 'pointer' }}>
                            เพิ่มรูป
                            <Form.Control 
                              type="file" 
                              accept="image/*" 
                              style={{ display: 'none' }} 
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setInvoiceImageFile(file);
                                  setInvoiceImagePreview(URL.createObjectURL(file));
                                }
                              }} 
                            />
                          </Form.Label>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Purchased Items Card */}
            <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold text-muted text-uppercase mb-0" style={{ fontSize: '0.8rem', letterSpacing: '1px' }}>Purchased Items</h6>
                  <Button size="sm" onClick={handleAddRow} className="px-3 py-1 rounded" style={darkBlueStyle}>
                    <FaPlus className="me-1"/> Add Row
                  </Button>
                </div>
                
                {formData.purchases.map((item, index) => (
                  <div key={index} className="p-3 mb-2 border rounded position-relative shadow-sm" style={{ backgroundColor: item.isNew ? "#f0f4f8" : "#ffffff", paddingRight: '35px' }}>
                    {formData.purchases.length > 1 && (
                      <button 
                        type="button" 
                        className="btn-close position-absolute top-0 end-0 m-2" 
                        onClick={() => handleRemoveRow(index)}
                        style={{ fontSize: '0.7rem' }}
                      ></button>
                    )}
                    
                    <Row className="g-2 align-items-end">
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small fw-medium text-muted mb-1 d-flex align-items-center">
                            <FaFilter className="me-1 text-primary"/> Category
                          </Form.Label>
                          <Select
                            placeholder="All"
                            isDisabled={item.inventory_item_id === 'NEW'}
                            options={[
                              { value: 'ALL', label: 'All' },
                              ...categories.map(c => ({ value: String(c.id), label: c.name }))
                            ]}
                            value={
                              (!item.filterCategoryId || item.filterCategoryId === 'ALL') 
                                ? { value: 'ALL', label: 'All' } 
                                : { value: item.filterCategoryId, label: categories.find(c => String(c.id) === item.filterCategoryId)?.name || 'Unknown' }
                            }
                            onChange={(selected) => handleChangePurchase(index, "filterCategoryId", selected.value)}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: '31px',
                                height: '31px',
                                fontSize: '0.875rem',
                                borderRadius: '0.25rem',
                                borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
                                boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
                                '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#dee2e6' }
                              }),
                              valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                              input: (base) => ({ ...base, margin: '0', padding: '0' }),
                              indicatorsContainer: (base) => ({ ...base, height: '29px' }),
                              menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 9999 })
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-medium text-muted mb-1">Item / Material</Form.Label>
                          <CreatableSelect
                            isClearable
                            placeholder="Select or type new item..."
                            options={itemOptions
                                .filter(inv => inv.id !== "NEW") // Exclude the old dummy NEW
                                .filter(inv => !item.filterCategoryId || item.filterCategoryId === 'ALL' || String(inv.category_id) === item.filterCategoryId)
                                .map(inv => ({ value: String(inv.id), label: `${inv.name} (${inv.unit})` }))
                            }
                            value={
                              item.inventory_item_id 
                                ? (item.isNew ? { value: item.name, label: item.name } : { value: String(item.inventory_item_id), label: `${inventoryItems.find(i => String(i.id) === String(item.inventory_item_id))?.name} (${item.unit})` }) 
                                : null
                            }
                            onChange={(selected) => handleChangePurchase(index, "inventory_item_obj", selected)}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: '31px',
                                height: '31px',
                                fontSize: '0.875rem',
                                borderRadius: '0.25rem',
                                borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
                                boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
                                '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#dee2e6' }
                              }),
                              valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                              input: (base) => ({ ...base, margin: '0', padding: '0' }),
                              indicatorsContainer: (base) => ({ ...base, height: '29px' }),
                              menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 9999 })
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={1}>
                        <Form.Group>
                          <Form.Label className="small fw-medium text-muted mb-1">Unit</Form.Label>
                          <Form.Control size="sm" type="text" value={item.unit} readOnly disabled className="bg-light" placeholder="-" />
                        </Form.Group>
                      </Col>

                      <Col md={1}>
                        <Form.Group>
                          <Form.Label className="small fw-medium text-muted mb-1">Qty</Form.Label>
                          <Form.Control size="sm" type="number" placeholder="Qty" min="0.01" step="0.01" value={item.quantity} onChange={e => handleChangePurchase(index, "quantity", e.target.value)} required />
                        </Form.Group>
                      </Col>

                      <Col>
                        <Form.Group>
                          <Form.Label className="small fw-medium text-muted mb-1">Total Cost (฿)</Form.Label>
                          <Form.Control size="sm" type="number" placeholder="0.00" min="0" step="0.01" value={item.total_cost || ""} onChange={e => handleChangePurchase(index, "total_cost", e.target.value)} required />
                        </Form.Group>
                      </Col>

                      <Col>
                        <Form.Group>
                          <Form.Label className="small fw-medium text-muted mb-1">Unit Cost (฿)</Form.Label>
                          <Form.Control size="sm" type="text" value={item.unit_cost ? Number(item.unit_cost).toFixed(2) : "-"} readOnly disabled className="bg-light text-muted" placeholder="0.00" />
                        </Form.Group>
                      </Col>

                      {currentUser?.allowed_branches && currentUser.allowed_branches.length > 1 && !item.isDistributed && (
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label className="small fw-medium text-muted mb-1">Branch</Form.Label>
                            <Form.Select size="sm" value={item.branch_id} onChange={e => handleChangePurchase(index, "branch_id", e.target.value)} required>
                              <option value="">Select...</option>
                              {currentUser.allowed_branches.map(b => {
                                const bId = b.id || b;
                                const bName = b.name || `Branch ${b}`;
                                return <option key={bId} value={bId}>{bName}</option>;
                              })}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>

                    {currentUser?.allowed_branches && currentUser.allowed_branches.length > 1 && (
                      <div className="mt-2 text-end">
                        <Button 
                          variant={item.isDistributed ? "warning" : "outline-primary"} 
                          size="sm" 
                          onClick={() => handleToggleDistribute(index)}
                          style={{ fontSize: '0.75rem' }}
                        >
                          {item.isDistributed ? "Cancel Distribute" : "เกลี่ยของ (Distribute)"}
                        </Button>
                      </div>
                    )}

                    {/* Distributions Section */}
                    {item.isDistributed && (
                      <div className="mt-3 pt-3 border-top border-primary border-opacity-25" style={{ backgroundColor: '#f8faff', padding: '10px', borderRadius: '8px' }}>
                        <h6 className="fw-bold mb-2 text-primary" style={{ fontSize: '0.8rem' }}>Distribute to Branches (Total Qty: {item.quantity || 0})</h6>
                        {item.distributions.map((dist, dIndex) => {
                          const cost = (Number(dist.quantity || 0) * Number(item.unit_cost || 0)).toFixed(2);
                          return (
                            <Row key={dIndex} className="g-2 mb-2 align-items-center">
                              <Col md={3}>
                                <Form.Select 
                                  size="sm" 
                                  value={dist.branch_id} 
                                  onChange={e => handleChangeDistribution(index, dIndex, "branch_id", e.target.value)} 
                                  required
                                >
                                  <option value="">Select Branch...</option>
                                  {currentUser.allowed_branches.map(b => {
                                    const bId = b.id || b;
                                    const bName = b.name || `Branch ${b}`;
                                    // Disable if already selected in another distribution for this item
                                    const isSelectedElseWhere = item.distributions.some((d, idx) => idx !== dIndex && String(d.branch_id) === String(bId));
                                    return <option key={bId} value={bId} disabled={isSelectedElseWhere}>{bName}</option>;
                                  })}
                                </Form.Select>
                              </Col>
                              <Col md={2}>
                                <Form.Control 
                                  size="sm" 
                                  type="number" 
                                  placeholder="Qty" 
                                  min="0.01" 
                                  step="0.01" 
                                  value={dist.quantity} 
                                  onChange={e => handleChangeDistribution(index, dIndex, "quantity", e.target.value)} 
                                  required 
                                />
                              </Col>
                              <Col md={2}>
                                <Form.Control 
                                  size="sm" 
                                  type="text" 
                                  value={cost > 0 ? `${cost} ฿` : "-"} 
                                  readOnly 
                                  disabled 
                                  className="bg-light text-muted" 
                                />
                              </Col>
                              <Col md={2}>
                                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveDistribution(index, dIndex)} style={{ padding: '0.15rem 0.4rem' }}>
                                  Remove
                                </Button>
                              </Col>
                            </Row>
                          );
                        })}
                        <Button variant="link" size="sm" className="p-0 mt-1 text-decoration-none" onClick={() => handleAddDistribution(index)}>
                          <FaPlus className="me-1"/> Add Branch
                        </Button>
                      </div>
                    )}

                    {/* Extra Row for NEW Item Details */}
                    {item.isNew && (
                      <div className="mt-3 pt-3 border-top border-secondary border-opacity-25">
                        <Row className="g-3 align-items-center">
                          <Col md={3}>
                            <h6 className="fw-bold mb-0" style={textDarkBlueStyle}>New Item Details</h6>
                          </Col>                          
                          <Col md={3}>
                            <Form.Group className="d-flex align-items-center">
                              <Form.Label className="small fw-medium text-muted mb-0 me-2 text-nowrap">Category</Form.Label>
                              <Form.Select size="sm" value={item.category_id} onChange={e => handleChangePurchase(index, "category_id", e.target.value)} required className="shadow-sm">
                                <option value="">Select Category...</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="d-flex align-items-center">
                              <Form.Label className="small fw-medium text-muted mb-0 me-2 text-nowrap">Type</Form.Label>
                              <Form.Select size="sm" value={item.type} onChange={e => handleChangePurchase(index, "type", e.target.value)} required className="shadow-sm">
                                <option value="direct_cost">Direct Cost</option>
                                <option value="sunk_cost">Sunk Cost</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="d-flex align-items-center">
                              <Form.Label className="small fw-medium text-muted mb-0 me-2 text-nowrap">Unit</Form.Label>
                              <Form.Control size="sm" type="text" placeholder="e.g. g, ml, pcs" value={item.unit} onChange={e => handleChangePurchase(index, "unit", e.target.value)} required className="shadow-sm" />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </div>
                ))}
              </Card.Body>
            </Card>

          </Modal.Body>
          <Modal.Footer className="border-0 pt-3 bg-light">
            <Button variant="outline-secondary" onClick={() => setShowAddModal(false)} className="px-4 rounded">Cancel</Button>
            <Button type="submit" className="px-4 shadow-sm rounded" style={darkBlueStyle}><FaCheck className="me-2"/> Save Invoice</Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
}
